import React from 'react';
import { Gift, ShoppingCart, Check, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { useStore, comboToProduct } from '../context/StoreContext';
import { CURRENCY } from '../mock/mock';
import { Reveal } from './Effects';
import { Bubbles } from './Decor';
import { toast } from 'sonner';

const ComboCard = ({ combo }) => {
  const { addToCart } = useStore();
  const soldOut = combo.stock <= 0;
  return (
    <div data-testid={`combo-card-${combo.id}`} className="group relative rounded-3xl overflow-hidden border border-primary/20 bg-gradient-to-br from-[#ecfdf3] via-white to-[#f0f9ff] shadow-[0_20px_60px_-25px_rgba(21,154,59,0.45)] grid md:grid-cols-[1.1fr_1fr]">
      <div className="relative h-64 md:h-auto min-h-[260px] flex items-center justify-center px-6 py-8 overflow-hidden">
        <div className="absolute inset-0 glow-pedestal" />
        <Bubbles count={8} />
        <div className="relative flex items-end justify-center">
          {combo.items.map((p, i) => (
            <img
              key={p.id}
              src={p.image}
              alt={p.name}
              className="combo-bottle h-40 md:h-52 w-auto object-contain drop-shadow-[0_14px_18px_rgba(0,0,0,0.2)]"
              style={{ marginLeft: i ? '-2.2rem' : 0, zIndex: 10 - Math.abs(i - (combo.items.length - 1) / 2), '--i': i }}
            />
          ))}
        </div>
        <span className="absolute top-5 left-5 bg-[#e11d1d] text-white font-display font-extrabold text-sm px-3 py-1.5 rounded-full shadow-lg">
          SAVE {combo.savePercent}%
        </span>
      </div>
      <div className="p-7 md:p-9 flex flex-col">
        <p className="text-primary font-semibold text-xs uppercase tracking-widest flex items-center gap-1.5"><Gift className="h-4 w-4" /> Combo Pack</p>
        <h3 className="font-display font-extrabold text-2xl md:text-3xl text-foreground mt-1">{combo.name}</h3>
        <p className="text-sm text-primary font-medium">{combo.tagline}</p>
        <p className="mt-3 text-sm text-muted-foreground">{combo.description}</p>
        <ul className="mt-4 grid grid-cols-2 gap-2">
          {combo.items.map((p) => (
            <li key={p.id} className="flex items-center gap-1.5 text-sm text-foreground/80"><Check className="h-4 w-4 text-primary shrink-0" /> {p.name}</li>
          ))}
        </ul>
        <div className="mt-auto pt-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-baseline gap-2">
              <span data-testid={`combo-price-${combo.id}`} className="font-display font-extrabold text-3xl text-foreground">{CURRENCY}{combo.finalPrice}</span>
              <span className="text-muted-foreground line-through">{CURRENCY}{combo.price}</span>
            </div>
            <p className="text-xs font-semibold text-[#159a3b]">You save {CURRENCY}{combo.savings}</p>
          </div>
          <Button
            data-testid={`add-combo-${combo.id}`}
            disabled={soldOut}
            onClick={() => { addToCart(combo); toast.success(`${combo.name} added to cart`); }}
            className="btn-shimmer bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-6"
          >
            <ShoppingCart className="h-4 w-4 mr-2" /> {soldOut ? 'Sold Out' : 'Add Combo'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CombosSection = () => {
  const { combos, products } = useStore();
  const list = combos.filter((c) => c.active).map((c) => comboToProduct(c, products)).filter((c) => c.items.length);
  if (!list.length) return null;
  return (
    <section id="combos" data-testid="combos-section" className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="mb-10">
          <p className="text-primary font-semibold uppercase tracking-wide text-sm mb-2 flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Bundle & Save</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground">Money-Saving <span className="gradient-text-animated">Combos</span></h2>
          <p className="mt-3 text-muted-foreground max-w-xl">Stock up on the essentials together and pay less for every bottle.</p>
        </Reveal>
        <div className="space-y-8">
          {list.map((c, i) => (
            <Reveal key={c.id} delay={i * 120}><ComboCard combo={c} /></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CombosSection;
